/**
 * `resolveRoomImage` (R23) — which plate the active room shows right now.
 *
 * @remarks
 * A pure derived read, the `deriveJournal` precedent: nothing is written,
 * nothing is stamped, and the transcript never learns a picture existed — the
 * record is prose, so the export is unchanged by this module's existence.
 * Replay safety follows for free: a re-render re-derives the same answer from
 * the same state, and a conditional plate's conditions evaluate through the
 * SAME `evaluateCondition` the one describer uses, at the same kind of
 * read-time boundary, so image and text can never disagree about what the
 * world is doing.
 *
 * First match wins over the variants, fallback otherwise — exactly
 * `resolveConditionalText`'s walk, over an image id instead of a string. The
 * walk is re-stated here rather than routed through the text resolver because
 * the payload type differs and a cast-through-string would trade a brand for
 * a lie; the shape is three lines, and the CONTRACT (ordered variants,
 * mandatory fallback, first match) is owned by the schema both readers parse.
 *
 * @packageDocumentation
 */

import type { ImageDefinition, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireRoom } from '../bundle/bundle-lookups.js';
import { firstMatchingVariant } from '../conditions/first-matching-variant.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';

/**
 * Resolve the active room's plate against the live world.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The plate to show, or `undefined` — a room without one renders exactly as it always has
 */
export const resolveRoomImage = (bundle: EngineGameBundle, state: RuntimeState): ImageDefinition | undefined => {
	const room = requireRoom(bundle, state.progression.activeRoomId);

	if (room.image === undefined) {
		return undefined;
	}

	const chosenImageId =
		typeof room.image === 'string'
			? room.image
			: (firstMatchingVariant(room.image.variants, (variant) => variant.condition, bundle, state)?.imageId ?? room.image.fallback);
	const image = bundle.indexes.imagesById.get(chosenImageId);

	// The loader's image gate resolves every reference before a bundle exists,
	// so a miss here is a corrupt bundle or a hand-built fixture lying about
	// its images — the requireItem/requireRoom judgment, plate-side.
	if (image === undefined) {
		throw new EngineInvariantError(
			`Image id "${chosenImageId}" has no definition in the bundle — the loader's image gate should have rejected this`,
		);
	}

	return image;
};
